/**
 * Sites Service
 * 
 * Service for fetching available sites data with caching mechanisms
 * for improved performance.
 */

import { apiClient } from '../api';
import { SitesResponse } from './types';

// Cache configuration
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const CACHE_KEY = 'sites_cache';

// Cache structure
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

/**
 * Checks if a cache entry is still valid
 */
const isCacheValid = (entry: CacheEntry<any>): boolean => {
  return Date.now() - entry.timestamp < CACHE_TTL;
};

/**
 * Sites Service with methods for fetching sites data
 */
export const sitesService = {

  /**
   * Retrieves all available sites with caching
   * @returns Promise resolving to array of site codes
   */
  async getSites(): Promise<string[]> {
    try {
      // Check if cached data exists and is valid
      const cachedItem = localStorage.getItem(CACHE_KEY);
      if (cachedItem) {
        const cached: CacheEntry<string[]> = JSON.parse(cachedItem);

        // Return cached data if still valid
        if (isCacheValid(cached)) {
          console.log('Using cached sites data');
          return cached.data;
        }

        // Remove expired cache
        localStorage.removeItem(CACHE_KEY);
      }
    } catch (error) {
      // If cache parsing fails, remove the corrupted entry
      localStorage.removeItem(CACHE_KEY);
      console.warn('Corrupted cache entry removed for sites:', error);
    }

    try {
      // Fetch fresh data from API
      console.log('Fetching fresh sites data from API');
      const response = await apiClient.get<SitesResponse>('/sites');

      // Cache the data with expiration
      const cacheData: CacheEntry<string[]> = {
        data: response.sites,
        timestamp: Date.now(),
      };

      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      } catch (storageError) {
        // Handle localStorage quota exceeded or other storage errors
        console.warn('Failed to cache sites data:', storageError);
      }

      return response.sites;

    } catch (error) {
      console.error('Failed to fetch sites data:', error);
      throw error;
    }
  },

  /**
   * Clears the sites cache
   */
  clearCache(): void {
    localStorage.removeItem(CACHE_KEY);
  },
};
